import { useState } from 'react';
import { motion } from 'framer-motion';
import { Stethoscope } from 'lucide-react';
import { Link } from 'react-router-dom';
import config from '../../config';
import { mediaUrl, useSiteContent } from '../../lib/siteContent';

const WhatsAppIcon = () => (
  <svg className="w-[17px] h-[17px]" viewBox="0 0 24 24" fill="#25D366" aria-hidden="true">
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
  </svg>
);

function DoctorCard({ doctor, index }) {
  const [expanded, setExpanded] = useState(false);
  const id = doctor.id || `doctor-${index}`;

  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ delay: index * 0.12 }}
      data-testid={`doctor-card-${id}`}
      className="bg-white rounded-3xl p-7 sm:p-8 shadow-[0_8px_30px_rgb(0,0,0,0.04)] border border-[#673c96]/8 hover:shadow-[0_12px_40px_rgb(0,0,0,0.08)] hover:-translate-y-1 transition-all duration-300 h-full flex flex-col"
    >
      <div className="flex items-start gap-5 mb-5">
        <div className="shrink-0 w-20 h-20 sm:w-24 sm:h-24 rounded-2xl overflow-hidden border border-[#673c96]/10 bg-[#f5f3f7]">
          {doctor.image ? (
            <img
              src={mediaUrl(doctor.image)}
              alt={doctor.alt || doctor.name}
              loading="lazy"
              className="w-full h-full object-cover object-top"
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center">
              <Stethoscope className="w-8 h-8 text-[#673c96]/40" />
            </div>
          )}
        </div>
        <div className="min-w-0 flex-1">
          <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-[#f37721]/8 mb-2">
            <Stethoscope className="w-3 h-3 text-[#f37721]" />
            <span className="text-[10px] font-bold uppercase tracking-[0.14em] text-[#f37721]" style={{ fontFamily: 'Montserrat, sans-serif' }}>
              {doctor.role}
            </span>
          </div>
          <h3 className="text-base sm:text-lg font-semibold text-[#311b52] leading-snug" style={{ fontFamily: 'Poppins, sans-serif' }}>
            {doctor.name}
          </h3>
          <p className="text-xs text-[#673c96]/70 mt-1.5 leading-relaxed" style={{ fontFamily: 'Montserrat, sans-serif' }}>
            {doctor.credentials}
          </p>
        </div>
      </div>

      <p
        className={`text-sm text-[#3e195e]/80 leading-relaxed flex-1 ${expanded ? '' : 'line-clamp-3 sm:line-clamp-none'}`}
        style={{ fontFamily: 'Montserrat, sans-serif' }}
      >
        {doctor.intro}
      </p>

      <button
        type="button"
        onClick={() => setExpanded(!expanded)}
        data-testid={`doctor-card-${id}-toggle`}
        className="sm:hidden text-xs font-semibold text-[#f37721] mt-3 self-start"
        style={{ fontFamily: 'Montserrat, sans-serif' }}
      >
        {expanded ? 'Show less' : 'Read more'}
      </button>
    </motion.div>
  );
}

export default function MedicalPartners() {
  const site = useSiteContent();
  const partners = site.partners || {};
  const doctors = Array.isArray(partners.doctors) ? partners.doctors.filter((d) => d && (d.name || d.intro)) : [];

  return (
    <section
      id="medical-partners"
      data-testid="medical-partners-section"
      className="bg-white py-20 md:py-28"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-12"
        >
          <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#f37721]" style={{ fontFamily: 'Montserrat, sans-serif' }}>
            {partners.eyebrow}
          </span>
          <h2
            className="text-3xl sm:text-4xl font-bold tracking-tight text-[#311b52] mt-4"
            style={{ fontFamily: 'Poppins, sans-serif' }}
          >
            {partners.headline}
          </h2>
          <p className="text-base text-[#3e195e]/70 mt-4 max-w-2xl mx-auto" style={{ fontFamily: 'Montserrat, sans-serif' }}>
            {partners.body}
          </p>
        </motion.div>

        {doctors.length > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8 mb-12">
            {doctors.map((doctor, i) => (
              <DoctorCard key={doctor.id || doctor.name || i} doctor={doctor} index={i} />
            ))}
          </div>
        )}

        <motion.div
          initial={{ opacity: 0, y: 10 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center"
        >
          <p className="text-sm text-[#3e195e]/70 mb-4" style={{ fontFamily: 'Montserrat, sans-serif' }}>
            {partners.cta_note}
          </p>
          <div className="flex flex-col items-center gap-3">
            <Link
              to="/vaccination-catalogue"
              className="inline-flex items-center justify-center gap-2 bg-[#f37721] hover:bg-[#d9651a] text-white rounded-full px-8 py-3 font-semibold text-sm"
              style={{ fontFamily: 'Montserrat, sans-serif' }}
            >
              {partners.cta_label || "Book now"}
            </Link>
            {config.WHATSAPP_CONFIGURED && (
              <a
                href={config.getWhatsAppLink()}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="medical-partners-whatsapp-btn"
                className="inline-flex items-center gap-1.5 text-[11px] font-medium text-[#673c96]/50 hover:text-[#673c96]"
                style={{ fontFamily: 'Montserrat, sans-serif' }}
              >
                <WhatsAppIcon />
                Book on WhatsApp
              </a>
            )}
          </div>
        </motion.div>
      </div>
    </section>
  );
}
